import {
  AgxBadge,
  AgxButton,
  AgxModal,
  AgxSpacer,
  AgxTypography,
  Icon_AlertCircleOutline,
} from '@urbanx/agx.ui-components2';
import {
  StaffPreviewLinkedDatesRequestSourceDateType,
  type LinkedDateImpact,
} from '@urbanx/agx.campaigns.client';
import './LinkedDateWarningModal.scss';

interface LinkedDateWarningModalProps {
  impactedDates: LinkedDateImpact[];
  sourceDateTypes: StaffPreviewLinkedDatesRequestSourceDateType[];
  onCancel: () => void;
  onConfirm: () => void;
}

const getSourceDateLabel = (sourceDateType: StaffPreviewLinkedDatesRequestSourceDateType) => {
  switch (sourceDateType) {
    case StaffPreviewLinkedDatesRequestSourceDateType.FinanceDate:
      return 'Finance date';
    case StaffPreviewLinkedDatesRequestSourceDateType.BuildingAndPestDate:
      return 'Building & Pest date';
    case StaffPreviewLinkedDatesRequestSourceDateType.ContractConditionChanges:
      return 'Unconditional date';
    case StaffPreviewLinkedDatesRequestSourceDateType.SpecialConditionDate:
      return 'Satisfaction of Special Condition';
    case StaffPreviewLinkedDatesRequestSourceDateType.ContractDate:
    default:
      return 'Contract date';
  }
};

export function LinkedDateWarningModal({
  impactedDates,
  sourceDateTypes,
  onCancel,
  onConfirm,
}: LinkedDateWarningModalProps) {
  const sourceDateLabel = sourceDateTypes.map(getSourceDateLabel).join(' and ');

  return (
    <AgxModal
      id="linked-date-warning-modal"
      variant="withoutDivider"
      type="single"
      minWidth="600px"
      setIsDialogOpen={(isOpen) => {
        if (!isOpen) onCancel();
      }}
      content={
        <AgxSpacer
          direction="vertical"
          gap="spacing-3xl"
          className="linked-date-warning-modal-content"
        >
          <AgxSpacer
            direction="vertical"
            gap="spacing-2xl"
            className="linked-date-warning-modal-header"
          >
            <AgxSpacer
              direction="horizontal"
              contentAlignment="center"
              contentJustification="center"
              className="linked-date-warning-modal-icon"
            >
              <Icon_AlertCircleOutline type="Danger" height={40} width={40} />
            </AgxSpacer>
            <AgxTypography
              id="linked-date-warning-modal-title"
              type="body_ui_strong"
              size="medium"
              color="var(--text-primary)"
            >
              Are you sure you want to change {sourceDateLabel}?
            </AgxTypography>
            <AgxTypography
              id="linked-date-warning-modal-description"
              type="body_ui"
              size="small"
              color="var(--text-secondary)"
            >
              If you change this date, the following due dates will also be affected:
            </AgxTypography>
          </AgxSpacer>
          <AgxSpacer
            direction="vertical"
            gap="spacing-lg"
            className="linked-date-warning-modal-list"
          >
            {impactedDates.map((impact) => (
              <AgxSpacer
                key={impact.key}
                direction="horizontal"
                gap="spacing-sm"
                contentAlignment="center"
                className="linked-date-warning-modal-row"
              >
                <AgxTypography
                  id={`linked-date-warning-modal-${impact.key}-label`}
                  type="body_ui"
                  size="small"
                  color="var(--text-primary)"
                >
                  <span style={{ opacity: 0.7 }}>&bull; {impact.label}</span>
                </AgxTypography>
                <AgxBadge
                  type="colourfill"
                  size="md"
                  colour="grey"
                  text={impact.relativeDateText}
                />
              </AgxSpacer>
            ))}
          </AgxSpacer>
        </AgxSpacer>
      }
      actionButtons={
        <AgxSpacer direction="horizontal" contentJustification="center" gap="spacing-lg" fill>
          <AgxButton
            id="linked-date-warning-cancel"
            variant="hollow"
            size="large"
            text="Cancel"
            stretch
            onClick={onCancel}
          />
          <AgxButton
            id="linked-date-warning-confirm"
            variant="primary"
            size="large"
            text="Confirm and edit"
            danger
            stretch
            onClick={onConfirm}
          />
        </AgxSpacer>
      }
      actionButtonContainerStyle="withDivider"
    />
  );
}
